import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Database,
  ExternalLink,
  Sparkles,
  X,
} from "lucide-react";
import { clsx } from "clsx";

import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
} from "@/features/power_projects/components/ui";

import {
  useEzAnalyze,
  useEzConfig,
  useEzHistoricalLoad,
  useEzIngestWindows,
  useEzPreview,
  useEzUpdateConfig,
} from "../hooks";
import type { CandidatesPreview, ProceedingRow } from "../types";
import { DeadlinePill, TagChip, fmtDate } from "../ui";

/* ── funnel visual ──────────────────────────────────────────────────────────── */

function FunnelNode({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: "muted" | "accent" | "ok" | "info";
  hint?: string;
}) {
  const toneCls = {
    muted: "text-[var(--color-text-muted)]",
    accent: "text-amber-600 dark:text-amber-400",
    ok: "text-emerald-600 dark:text-emerald-400",
    info: "text-sky-600 dark:text-sky-400",
  }[tone];
  return (
    <div
      className="flex flex-col items-center text-center min-w-[92px]"
      title={hint}
    >
      <span
        className={clsx(
          "text-2xl font-bold tabular-nums leading-none",
          toneCls,
        )}
      >
        {value.toLocaleString("pl-PL")}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mt-1.5 leading-tight">
        {label}
      </span>
    </div>
  );
}

function Arrow({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center px-1 shrink-0">
      <ArrowRight size={16} className="text-[var(--color-text-muted)]" />
      {label && (
        <span className="text-[9px] text-[var(--color-text-muted)] mt-0.5 whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
  );
}

function Funnel({ data }: { data: CandidatesPreview }) {
  const s = data.stages;
  const total =
    (s.rejected_rules ?? 0) +
    (s.s1_candidate ?? 0) +
    (s.s1_rejected ?? 0) +
    (s.s1_classified ?? 0) +
    (s.s2_qualified ?? 0);
  return (
    <div className="flex items-center gap-2 md:gap-4 overflow-x-auto py-1">
      <FunnelNode
        label="W bazie"
        value={total}
        tone="muted"
        hint="Wszystkie zaciągnięte postępowania"
      />
      <Arrow label="reguły S0 (darmowe)" />
      <FunnelNode
        label="Odpadło na regułach"
        value={s.rejected_rules ?? 0}
        tone="muted"
        hint="Dostawy, CPV poza whitelistą, blacklista słów"
      />
      <FunnelNode
        label="Czeka na AI"
        value={s.s1_candidate ?? 0}
        tone="accent"
        hint="Kandydaci do taniej klasyfikacji S1 — bramka wydatkowa poniżej"
      />
      <Arrow label="klasyfikacja S1 (DeepSeek)" />
      <FunnelNode
        label="Odrzucone przez AI"
        value={s.s1_rejected ?? 0}
        tone="muted"
      />
      <FunnelNode
        label="Poza profilem"
        value={s.s1_classified ?? 0}
        tone="info"
      />
      <FunnelNode
        label="Priorytet"
        value={s.s2_qualified ?? 0}
        tone="ok"
        hint="Branże priorytetowe — trafiają na górę feedu"
      />
    </div>
  );
}

/* ── spend gate: candidate list with selection ──────────────────────────────── */

function CostChip({ cost, model }: { cost: string; model: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-800 whitespace-nowrap"
      title={`Szacunek na cenniku modelu ${model}`}
    >
      <Sparkles size={11} /> ~${cost} · {model}
    </span>
  );
}

function CandidateRow({
  row,
  checked,
  onToggle,
}: {
  row: ProceedingRow;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={clsx(
        "flex items-start gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors",
        checked
          ? "border-rose-400 bg-rose-50/60 dark:bg-rose-900/20"
          : "border-[var(--color-border)] hover:bg-black/[.02] dark:hover:bg-white/[.04]",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-1 accent-rose-600"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">{row.title}</p>
        <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 truncate">
          {row.organization_name} · {row.organization_city} (
          {row.organization_province})
          {row.cpv_main && <> · CPV {row.cpv_main}</>}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <DeadlinePill row={row} />
        {row.source_url && (
          <a
            href={row.source_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[var(--color-text-muted)] hover:text-rose-600"
            title="Otwórz na ezamowienia.gov.pl"
          >
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    </label>
  );
}

function SpendGate({ data }: { data: CandidatesPreview }) {
  const analyze = useEzAnalyze();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const sample = data.sample;
  const openGroup = data.candidates.open;
  const histGroup = data.candidates.historical;
  const totalCandidates = openGroup.count + histGroup.count;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisible = useMemo(() => sample.map((r) => r.id), [sample]);
  const allChecked =
    selected.size > 0 && allVisible.every((id) => selected.has(id));

  if (totalCandidates === 0)
    return (
      <SectionCard title="Bramka wydatkowa AI" className="mb-4">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Brak kandydatów do klasyfikacji — wszystko, co przeszło reguły, jest
          już ocenione. Nowi kandydaci pojawią się po nocnym ingeście lub
          loadzie historycznym.
        </p>
      </SectionCard>
    );

  return (
    <SectionCard
      title="Bramka wydatkowa AI — co EZ-ARON chce przeanalizować"
      className="mb-4"
      action={
        analyze.isSuccess ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
            <Check size={13} /> zlecono — wyniki spłyną do feedu
          </span>
        ) : undefined
      }
    >
      <p className="text-xs text-[var(--color-text-muted)] mb-3">
        Klasyfikacja S1 (projekt/budowa + tagi branż) na najtańszym modelu.
        Dopóki nie klikniesz — moduł nie wyda ani tokena. Twarde limity kosztu
        na run pilnują budżetu.
      </p>
      {analyze.isError && (
        <p className="text-xs text-red-600 mb-3">
          {(
            analyze.error as {
              response?: { data?: { errors?: { detail?: string } } };
            }
          )?.response?.data?.errors?.detail ??
            "Nie udało się zlecić klasyfikacji — spróbuj ponownie."}
        </p>
      )}

      {/* the two scopes as clear action rows */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-[var(--color-border)] px-3 py-2.5">
          <div className="min-w-0">
            <p className="text-sm font-medium">
              Otwarte postępowania{" "}
              <span className="text-[var(--color-text-muted)] font-normal">
                — zasilą feed okazji
              </span>
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {openGroup.count} szt. ·{" "}
              {openGroup.by_cpv_division
                .slice(0, 4)
                .map((r) => `CPV ${r.division}: ${r.n}`)
                .join(" · ") || "—"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CostChip
              cost={openGroup.estimate.cost_usd}
              model={openGroup.estimate.model}
            />
            <Button
              disabled={analyze.isPending || openGroup.count === 0}
              onClick={() => analyze.mutate({ scope: "open" })}
            >
              Analizuj otwarte ({openGroup.count})
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-[var(--color-border)] px-3 py-2.5">
          <div className="min-w-0">
            <p className="text-sm font-medium">
              Historyczne / rozstrzygnięte{" "}
              <span className="text-[var(--color-text-muted)] font-normal">
                — tagi do benchmarków cen
              </span>
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {histGroup.count} szt. ·{" "}
              {histGroup.by_province
                .slice(0, 4)
                .map((r) => `${r.organization_province || "?"}: ${r.n}`)
                .join(" · ") || "—"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CostChip
              cost={histGroup.estimate.cost_usd}
              model={histGroup.estimate.model}
            />
            <Button
              variant="secondary"
              disabled={analyze.isPending}
              onClick={() => analyze.mutate({ scope: "all" })}
            >
              Analizuj wszystko ({totalCandidates})
            </Button>
          </div>
        </div>
      </div>

      {/* selective mode */}
      {sample.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-rose-700 dark:text-rose-400 select-none">
            Wybierz pojedynczo ({sample.length}
            {totalCandidates > sample.length ? ` z ${totalCandidates}` : ""} na
            liście)
          </summary>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() =>
                    setSelected(allChecked ? new Set() : new Set(allVisible))
                  }
                  className="accent-rose-600"
                />
                Zaznacz wszystkie widoczne
              </label>
              <Button
                disabled={analyze.isPending || selected.size === 0}
                onClick={() => analyze.mutate({ ids: Array.from(selected) })}
              >
                Analizuj zaznaczone ({selected.size})
              </Button>
            </div>
            {sample.map((row) => (
              <CandidateRow
                key={row.id}
                row={row}
                checked={selected.has(row.id)}
                onToggle={() => toggle(row.id)}
              />
            ))}
          </div>
        </details>
      )}
    </SectionCard>
  );
}

/* ── historical load ────────────────────────────────────────────────────────── */

function HistoricalLoad() {
  const load = useEzHistoricalLoad();
  const [resultsMonths, setResultsMonths] = useState(24);
  const [contractsMonths, setContractsMonths] = useState(7);
  return (
    <SectionCard
      title="Darmowy load historyczny"
      className="mb-4"
      action={
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-600">
          <Database size={12} /> zero AI · zero kosztu
        </span>
      }
    >
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
        <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
          Wyniki postępowań (ceny zwycięskie, liczby ofert, wykonawcy) parsuje
          deterministyczny parser — to fundament benchmarków cenowych.
          Ogłoszenia o zamówieniu zasilą feed. Po zakończeniu wróć tu i zdecyduj
          przy bramce wydatkowej, co analizować AI.
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-xs text-[var(--color-text-secondary)]">
            Wyniki
            <input
              type="number"
              min={0}
              max={60}
              value={resultsMonths}
              onChange={(e) => setResultsMonths(Number(e.target.value))}
              className="block w-20 mt-1 border border-[var(--color-border)] rounded-lg px-2 py-1.5 bg-transparent tabular-nums"
            />
            <span className="text-[10px] text-[var(--color-text-muted)]">
              mies. wstecz
            </span>
          </label>
          <label className="text-xs text-[var(--color-text-secondary)]">
            Ogłoszenia
            <input
              type="number"
              min={0}
              max={24}
              value={contractsMonths}
              onChange={(e) => setContractsMonths(Number(e.target.value))}
              className="block w-20 mt-1 border border-[var(--color-border)] rounded-lg px-2 py-1.5 bg-transparent tabular-nums"
            />
            <span className="text-[10px] text-[var(--color-text-muted)]">
              mies. wstecz
            </span>
          </label>
          <Button
            disabled={load.isPending}
            onClick={() =>
              load.mutate({
                results_months: resultsMonths,
                contracts_months: contractsMonths,
              })
            }
          >
            {load.isPending ? "Zlecanie…" : "Uruchom load"}
          </Button>
        </div>
      </div>
      {load.isSuccess && (
        <p className="text-xs text-emerald-600 mt-2 inline-flex items-center gap-1">
          <Check size={13} /> Load zlecony — postęp w oknach ingestu poniżej
          (odświeżają się same).
        </p>
      )}
      {load.isError && (
        <p className="text-xs text-red-600 mt-2">
          Nie udało się zlecić loadu — spróbuj ponownie.
        </p>
      )}
    </SectionCard>
  );
}

/* ── config chips ───────────────────────────────────────────────────────────── */

function ChipListEditor({
  label,
  hint,
  value,
  onSave,
  saving,
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string[];
  onSave: (items: string[]) => void;
  saving: boolean;
  placeholder: string;
}) {
  const [items, setItems] = useState<string[] | null>(null);
  const [draft, setDraft] = useState("");
  const current = items ?? value;
  const dirty =
    items !== null && JSON.stringify(items) !== JSON.stringify(value);

  const add = () => {
    const v = draft.trim().replace(/,+$/, "");
    if (!v) return;
    if (!current.includes(v)) setItems([...current, v]);
    setDraft("");
  };

  return (
    <div className="py-2.5 border-b border-[var(--color-border)] last:border-b-0">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium">{label}</p>
        {hint && (
          <p className="text-[10px] text-[var(--color-text-muted)]">{hint}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        {current.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 text-[11px] rounded-full pl-2 pr-1 py-0.5 bg-black/5 dark:bg-white/10"
          >
            {v}
            <button
              onClick={() => setItems(current.filter((x) => x !== v))}
              className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/20"
              aria-label={`Usuń ${v}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
          placeholder={placeholder}
          className="text-[11px] border border-dashed border-[var(--color-border)] rounded-full px-2.5 py-0.5 bg-transparent min-w-36 focus:outline-none focus:border-rose-400"
        />
        {dirty && (
          <>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => onSave(current)}
            >
              Zapisz
            </Button>
            <button
              onClick={() => setItems(null)}
              className="text-[11px] text-[var(--color-text-muted)] hover:underline"
            >
              cofnij
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── ingest windows ─────────────────────────────────────────────────────────── */

const NOTICE_TYPE_LABEL: Record<string, string> = {
  ContractNotice: "Ogłoszenia o zamówieniu",
  NoticeUpdateNotice: "Zmiany ogłoszeń",
  TenderResultNotice: "Wyniki postępowań",
  ContractPerformingNotice: "Wykonanie umów",
};

function IngestWindows() {
  const windows = useEzIngestWindows();
  const rows = windows.data ?? [];
  return (
    <SectionCard title="Okna ingestu — monitoring luk">
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          Jeszcze nic nie pobrano — uruchom load historyczny powyżej. Nocny
          ingest (03:20) będzie kontynuował od ostatniego udanego okna.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="py-1.5 pr-3 font-medium">Zakres</th>
                <th className="py-1.5 pr-3 font-medium">Okno</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 pr-3 font-medium text-right">Pobrane</th>
                <th className="py-1.5 font-medium text-right">Nowe</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr
                  key={w.id}
                  className="border-b border-[var(--color-border)] last:border-b-0"
                >
                  <td className="py-1.5 pr-3 text-xs">
                    {NOTICE_TYPE_LABEL[w.notice_type] ?? w.notice_type}
                  </td>
                  <td className="py-1.5 pr-3 text-xs tabular-nums text-[var(--color-text-muted)]">
                    {fmtDate(w.date_from)} → {fmtDate(w.date_to)}
                  </td>
                  <td className="py-1.5 pr-3">
                    <span
                      title={w.error || undefined}
                      className={clsx(
                        "text-[10px] font-medium rounded-full px-2 py-0.5",
                        w.status === "done"
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                          : w.status === "running"
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                      )}
                    >
                      {w.status === "done"
                        ? "OK"
                        : w.status === "running"
                          ? "w toku"
                          : "błąd"}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-xs">
                    {w.fetched_count}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-xs font-medium">
                    {w.created_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/* ── page ───────────────────────────────────────────────────────────────────── */

export default function EzCandidatesPage() {
  const preview = useEzPreview();
  const config = useEzConfig();
  const updateConfig = useEzUpdateConfig();

  if (preview.isLoading) return <PageLoader />;
  const data = preview.data;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Kandydaci do analizy AI"
        subtitle="Load i ingest są darmowe. Tu decydujesz, na co EZ-ARON wyda tokeny."
        backTo="/ezamowienia"
        backLabel="Oferty przetargowe"
      />

      {data && (
        <>
          <SectionCard title="Lejek preselekcji" className="mb-4">
            <Funnel data={data} />
          </SectionCard>
          <SpendGate data={data} />
        </>
      )}

      <HistoricalLoad />

      <SectionCard
        title="Profil zainteresowań — reguły S0 i priorytety S2"
        className="mb-4"
        bodyClassName="pt-1 pb-2"
      >
        <ChipListEditor
          label="Prefiksy CPV (whitelist S0)"
          hint="45 = roboty budowlane, 71 = usługi projektowe"
          value={config.data?.cpv_prefixes ?? []}
          saving={updateConfig.isPending}
          placeholder="+ dodaj prefiks…"
          onSave={(items) => updateConfig.mutate({ cpv_prefixes: items })}
        />
        <ChipListEditor
          label="Blacklista słów w tytule"
          hint="odrzuca na regułach, zanim AI cokolwiek zobaczy"
          value={config.data?.keyword_blacklist ?? []}
          saving={updateConfig.isPending}
          placeholder="+ np. kotłowni…"
          onSave={(items) => updateConfig.mutate({ keyword_blacklist: items })}
        />
        <ChipListEditor
          label="Województwa (puste = całe PL)"
          hint="kody NUTS, np. PL08"
          value={config.data?.province_whitelist ?? []}
          saving={updateConfig.isPending}
          placeholder="+ np. PL08…"
          onSave={(items) => updateConfig.mutate({ province_whitelist: items })}
        />
        <ChipListEditor
          label="Branże priorytetowe (S2)"
          hint="te tagi wynoszą postępowanie na górę feedu"
          value={config.data?.priority_tags ?? []}
          saving={updateConfig.isPending}
          placeholder="+ np. oze_pv…"
          onSave={(items) => updateConfig.mutate({ priority_tags: items })}
        />
        <div className="pt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-[var(--color-text-muted)]">
            Dostępne tagi:
          </span>
          {(config.data?.priority_tags?.length ?? 0) >= 0 &&
            [
              "elektroenergetyka",
              "oswietlenie",
              "stacje_trafo",
              "oze_pv",
              "magazyny_energii",
              "ladowarki_ev",
              "elektryka_kubaturowa",
              "teletechnika",
              "drogowe",
              "sanitarne",
              "kubatura_ogolna",
            ].map((t) => <TagChip key={t} tag={t} />)}
        </div>
      </SectionCard>

      <IngestWindows />

      <p className="text-[11px] text-[var(--color-text-muted)] mt-4">
        Postęp zadań w tle znajdziesz w panelu zadań; wyniki klasyfikacji
        spływają do{" "}
        <Link
          to="/ezamowienia"
          className="text-rose-700 dark:text-rose-400 hover:underline"
        >
          feedu okazji
        </Link>
        , a preferencje EZ-ARONA uczysz w{" "}
        <Link
          to="/ezamowienia/knowledge"
          className="text-rose-700 dark:text-rose-400 hover:underline"
        >
          bazie wiedzy
        </Link>
        .
      </p>
    </div>
  );
}
